import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../services/utils";
import {
  addAuthTagSchema,
  editAuthTagSchema,
} from "../../schemas/AuthTag.schema";
import {
  createUserAuthTag,
  updateUserAuthTag,
} from "../../services/authTagService";
import useFetchAuthTagById from "../../hooks/useFetchAuthTagById";
import FlexBetween from "../../components/FlexBetween";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import Loader from "../../components/Molecules/Loader/Loader";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";

const AddEditAuthTag: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Fetch tag data if editing
  const [authTagData] = useFetchAuthTagById({
    setIsLoading,
    authTagId: isEditMode && id ? id : null,
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: isEditMode ? editAuthTagSchema : addAuthTagSchema,
    enableReinitialize: true,
    onSubmit: async (payload) => {
      try {
        setIsLoading(true);

        if (isEditMode) {
          // Update existing tag
          await updateUserAuthTag(id!, payload);
          showToast("Tag updated successfully", "success");
        } else {
          // Create new tag
          await createUserAuthTag(payload);
          showToast("Tag created successfully", "success");
        }

        navigate("/tags");
      } catch (error: any) {
        console.error("Failed to save tag:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Populate form with tag data when editing
  useEffect(() => {
    if (isEditMode && authTagData) {
      const values = {
        name: authTagData?.name || "",
        description: authTagData?.description || "",
      };
      formik.setValues(values);
      setInitialValues(values);
    }
  }, [authTagData, isEditMode]);

  // Track form changes
  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(formik.values).length > 0) {
      const hasFormChanges =
        JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [formik.values, initialValues]);

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode ? `/auth-tag/${id}` : "/auth-tag",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(JSON.stringify(formik.values, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
              <label className="label-text">
                Tag Name<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.name && formik.errors.name ? "has-error" : ""
                }`}
                error={!!(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                value={formik.values.name}
                placeholder="Enter tag name"
              />
            </Grid>

            <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
              <label className="label-text">Description</label>
              <TextField
                type="text"
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.description && formik.errors.description
                    ? "has-error"
                    : ""
                }`}
                error={
                  !!(formik.touched.description && formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                value={formik.values.description}
                placeholder="Enter description"
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {/* <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={() => navigate("/tags")}
              className="btn-orange btn-dark"
            >
              Cancel
            </Button> */}
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp; {isEditMode ? "Update" : "Save"}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Tag JSON"
        payload={formik.values}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditAuthTag;
